import type { FileItem } from "@/contexts/FileUploadContext";

// Helper function to check if any attachments are incomplete
export const hasIncompleteAttachments = (attachments: FileItem[]): boolean => {
  return attachments.some((attachment) => attachment.status !== "COMPLETED");
};

// Helper function to check if we should disable file uploads
// Disable when files are uploading/processing but no feed_id exists yet
export const shouldDisableFileUpload = (
  attachments: FileItem[],
  feedId: string | null
): boolean => {
  const hasUploadingFiles = attachments.some((attachment) =>
    ["UPLOADING", "UPLOADED"].includes(attachment.status)
  );

  // If we have uploading files but no feed_id, disable upload
  return hasUploadingFiles && !feedId;
};

// Helper function to simplify MIME type for user-friendly display
export const simplifyMimeType = (mimeType: string): string => {
  if (!mimeType) return "unknown";

  // Common MIME type mappings to user-friendly names
  const mimeTypeMap: Record<string, string> = {
    // Documents
    "application/pdf": "PDF",
    "application/msword": "Word",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word",
    "application/vnd.ms-excel": "Excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Excel",
    "application/vnd.ms-powerpoint": "PowerPoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "PowerPoint",

    // Text files
    "text/plain": "Text",
    "text/markdown": "Markdown",
    "text/csv": "CSV",
    "text/html": "HTML",
    "text/css": "CSS",
    "application/json": "JSON",
    "application/xml": "XML",
    "text/xml": "XML",

    // Images
    "image/jpeg": "JPEG",
    "image/jpg": "JPEG",
    "image/png": "PNG",
    "image/gif": "GIF",
    "image/svg+xml": "SVG",
    "image/webp": "WebP",

    // Archives
    "application/zip": "ZIP",
    "application/x-rar-compressed": "RAR",
    "application/x-7z-compressed": "7Z",

    // Code files
    "application/javascript": "JavaScript",
    "text/javascript": "JavaScript",
    "application/typescript": "TypeScript",
    "text/typescript": "TypeScript",
  };

  // Check for exact match first
  if (mimeTypeMap[mimeType]) {
    return mimeTypeMap[mimeType];
  }

  // Extract main type (e.g., "text" from "text/plain")
  const mainType = mimeType.split("/")[0];
  switch (mainType) {
    case "text":
      return "Text file";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "application":
      return "Application file";
    default:
      return mimeType; // Fallback to original if no mapping found
  }
};
